import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Modal, Form, Select, Input, message, Tabs, Space, Descriptions, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { invoiceAPI, collaboratorAPI } from '../services/api';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [allCollaborators, setAllCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchInvoiceDetail();
    fetchAllCollaborators();
  }, [id]);

  const fetchInvoiceDetail = async () => {
    setLoading(true);
    try {
      const response = await invoiceAPI.getById(id);
      setInvoice(response.data.invoice);
      setCollaborators(response.data.collaborators);
    } catch (error) {
      message.error('Lỗi khi tải chi tiết invoice');
    }
    setLoading(false);
  };

  const fetchAllCollaborators = async () => {
    try {
      const response = await collaboratorAPI.getAll();
      setAllCollaborators(response.data);
    } catch (error) {
      message.error('Lỗi khi tải danh sách cộng tác viên');
    }
  };

  const handleEdit = (record) => {
    if (!invoice.isActive) {
      message.error('Invoice đã bị khóa, không thể chỉnh sửa');
      return;
    }
    setEditingRowId(record._id);
    setEditingData({
      [record._id]: {
        appSpentCtv: record.appSpentCtv,
        vpcsSpentCtv: record.vpcsSpentCtv,
        appCttCtv: record.appCttCtv,
        vpcsCttCtv: record.vpcsCttCtv
      }
    });
  };

  const handleRemoveCollaborator = async (relationId) => {
    if (!invoice.isActive) {
      message.error('Invoice đã bị khóa, không thể xóa CTV');
      return;
    }
    try {
      await invoiceAPI.removeCollaborator(relationId);
      message.success('Xóa cộng tác viên thành công');
      fetchInvoiceDetail();
    } catch (error) {
      message.error('Lỗi khi xóa cộng tác viên');
    }
  };

  const handleAddCollaborator = async (values) => {
    if (!invoice.isActive) {
      message.error('Invoice đã bị khóa, không thể thêm CTV');
      return;
    }
    try {
      await invoiceAPI.addCollaborator({
        invoiceId: id,
        collaboratorId: values.collaboratorId
      });
      message.success('Thêm cộng tác viên thành công');
      setModalVisible(false);
      form.resetFields();
      fetchInvoiceDetail();
    } catch (error) {
      message.error('Lỗi khi thêm cộng tác viên');
    }
  };

  const handleCellEdit = (record, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [record._id]: {
        ...prev[record._id],
        [field]: value
      }
    }));
  };



  const handleSave = async (record) => {
    try {
      const updatedData = editingData[record._id] || {};
      const response = await invoiceAPI.updateCollaborator(record._id, {
        appSpentCtv: updatedData.appSpentCtv ?? record.appSpentCtv,
        vpcsSpentCtv: updatedData.vpcsSpentCtv ?? record.vpcsSpentCtv,
        appCttCtv: updatedData.appCttCtv ?? record.appCttCtv,
        vpcsCttCtv: updatedData.vpcsCttCtv ?? record.vpcsCttCtv
      });
      message.success('Lưu thành công - Funds của CTV đã được cập nhật');
      setEditingRowId(null);
      setEditingData({});
      await fetchInvoiceDetail();
    } catch (error) {
      console.error('Lỗi khi lưu:', error);
      message.error(error.response?.data?.error || 'Lỗi khi lưu dữ liệu');
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const EditableCell = ({ record, field }) => {
    const isEditing = editingRowId === record._id;
    const value = editingData[record._id]?.[field] ?? record[field];
    
    if (isEditing) {
      return (
        <Input
          value={value}
          onChange={(e) => handleCellEdit(record, field, parseFloat(e.target.value) || 0)}
          style={{ width: '100%' }}
          placeholder="Nhập số"
        />
      );
    }
    
    return <span>${value?.toLocaleString()}</span>;
  };

  const columns = [
    {
      title: 'Telegram CTV',
      dataIndex: ['collaboratorId', 'tele'],
      key: 'tele',
      fixed: 'left',
      width: 120
    },
    {
      title: 'APP Tiêu thụ',
      dataIndex: 'appSpentCtv',
      key: 'appSpentCtv',
      width: 150,
      render: (_, record) => <EditableCell record={record} field="appSpentCtv" />
    },
    {
      title: 'VPCS Tiêu thụ',
      dataIndex: 'vpcsSpentCtv',
      key: 'vpcsSpentCtv',
      width: 150,
      render: (_, record) => <EditableCell record={record} field="vpcsSpentCtv" />
    },
    {
      title: 'APP Chi tiêu trước',
      dataIndex: 'appCttCtv',
      key: 'appCttCtv',
      width: 150,
      render: (_, record) => <EditableCell record={record} field="appCttCtv" />
    },
    {
      title: 'VPCS Chi tiêu trước',
      dataIndex: 'vpcsCttCtv',
      key: 'vpcsCttCtv',
      width: 150,
      render: (_, record) => <EditableCell record={record} field="vpcsCttCtv" />
    },
    {
      title: 'Tổng tiêu thụ',
      dataIndex: 'totalSpentCtv',
      key: 'totalSpentCtv',
      width: 130,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Rate APP',
      dataIndex: 'rateAppSpentCtv',
      key: 'rateAppSpentCtv',
      width: 120,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Rate VPCS',
      dataIndex: 'rateVpcsSpentCtv',
      key: 'rateVpcsSpentCtv',
      width: 120,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Tổng Rate',
      dataIndex: 'totalRateSpentCtv',
      key: 'totalRateSpentCtv',
      width: 130,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right',
      width: 220,
      render: (_, record) => {
        const isEditing = editingRowId === record._id;
        
        return (
          <Space>
            {isEditing ? (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave(record)}
                >
                  Lưu
                </Button>
                <Button
                  size="small"
                  onClick={handleCancelEdit}
                >
                  Hủy
                </Button>
              </>
            ) : (
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
            )}
            <Popconfirm
              title="Xác nhận xóa?"
              onConfirm={() => handleRemoveCollaborator(record._id)}
            >
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  if (!invoice) return <div style={{ padding: 24 }}>Đang tải...</div>;

  const items = [
    {
      key: '1',
      label: 'Thông tin Invoice',
      children: (
        <Card>
          {!invoice.isActive && (
            <div style={{ 
              padding: '10px', 
              marginBottom: '16px', 
              backgroundColor: '#fff2e8', 
              border: '1px solid #ffbb96',
              borderRadius: '4px',
              color: '#d4380d',
              fontWeight: 'bold'
            }}>
              ⚠️ Invoice này đã bị khóa. Không thể chỉnh sửa dữ liệu.
            </div>
          )}
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Mã Invoice">{invoice.code}</Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              <span style={{ color: invoice.isActive ? 'green' : 'red', fontWeight: 'bold' }}>
                {invoice.isActive ? 'Đang hoạt động' : 'Đã khóa'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">
              {new Date(invoice.createdAt).toLocaleDateString('vi-VN')}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày kết thúc">
              {invoice.endedAt ? new Date(invoice.endedAt).toLocaleDateString('vi-VN') : 'Chưa kết thúc'}
            </Descriptions.Item>
            <Descriptions.Item label="Rate APP CTV">{invoice.rateAppCtv}%</Descriptions.Item>
            <Descriptions.Item label="Rate VPCS CTV">{invoice.rateVpcsCtv}%</Descriptions.Item>
            <Descriptions.Item label="Rate APP Chủ">{invoice.rateAppMain}%</Descriptions.Item>
            <Descriptions.Item label="Rate VPCS Chủ">{invoice.rateVpcsMain}%</Descriptions.Item>
            <Descriptions.Item label="Tổng tiêu thụ Chủ" span={2}>
              ${invoice.finalSpentMain?.toLocaleString()} ( APP: ${invoice.appSpentMain?.toLocaleString()} + VPCS: ${invoice.vpcsSpentMain?.toLocaleString()} )
            </Descriptions.Item>
            <Descriptions.Item label="Tổng Rate Chủ" span={2}>
              ${invoice.finalRateSpentMain?.toLocaleString()} ( APP: ${invoice.rateAppSpentMain?.toLocaleString()} + VPCS: ${invoice.rateVpcsSpentMain?.toLocaleString()} )
            </Descriptions.Item>
            <Descriptions.Item label="Lợi nhuận" span={2}>
              <span style={{ color: 'green', fontWeight: 'bold', fontSize: 16 }}>
                ${invoice.totalProfit?.toLocaleString()}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )
    },
    {
      key: '2',
      label: 'Danh sách CTV',
      children: (
        <div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
            style={{ marginBottom: 16 }}
            disabled={!invoice.isActive}
          >
            Thêm Cộng tác viên
          </Button>
          <Table
            columns={columns}
            dataSource={collaborators}
            loading={loading}
            rowKey="_id"
            scroll={{ x: 1800 }}
            pagination={{ pageSize: 10 }}
            title={() => <strong>Bảng chi tiêu của các CTV trong Invoice này</strong>}
            summary={(pageData) => {
              let totalApp = 0, totalVpcs = 0, totalSpent = 0;
              let totalRateApp = 0, totalRateVpcs = 0;

              pageData.forEach((record) => {
                totalApp += record.appSpentCtv || 0;
                totalVpcs += record.vpcsSpentCtv || 0;
                totalSpent += record.totalSpentCtv || 0;
                totalRateApp += record.rateAppSpentCtv || 0;
                totalRateVpcs += record.rateVpcsSpentCtv || 0;
              });

              return (
                <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>${totalApp.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>${totalVpcs.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5}>${totalSpent.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>${totalRateApp.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>${totalRateVpcs.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8} />
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>
          Quay lại
        </Button>
        <h1 style={{ margin: 0 }}>{invoice.code}</h1>
      </Space>

      <Tabs items={items} />

      <Modal
        title="Thêm Cộng tác viên vào Invoice"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleAddCollaborator} layout="vertical">
          <Form.Item
            name="collaboratorId"
            label="Chọn Cộng tác viên"
            rules={[{ required: true, message: 'Vui lòng chọn cộng tác viên' }]}
          >
            <Select
              showSearch
              placeholder="Chọn cộng tác viên"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {allCollaborators
                .filter(c => !collaborators.find(col => col.collaboratorId._id === c._id))
                .map(c => (
                  <Select.Option key={c._id} value={c._id}>
                    {c.tele}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Thêm
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoiceDetail;
